// Event kinds — single source of truth for icons, labels, tint.
// Tints rebalanced 2026-04 — pushed more chroma into the cool/warm
// pairs that were drifting toward each other (school_event vs travel
// were both pale blues; day_trip vs dinner vs appointment were three
// warm beiges) so teen-mode dimming still leaves them distinguishable.
import { fmtTime } from "@/lib/time";

export const EVENT_KINDS = [
  // Refreshed palette (2026-05) — kids-mode pastels. Lifted to ~L=90%
  // (was 85%) with higher saturation so colors read as candy-bright
  // pastels rather than muted vintage. Yellow especially gets a clear
  // butter-yellow lift; "other" swapped from dirty brown to a soft
  // cream so virtual all-day cards (Mors dag, holidays) feel light.
  // `label_da` is the Danish rendering — resolved via `kindLabel(kind, lang)`
  // downstream so the picker/filters follow the user's locale.
  { key: "activity", label: "Sport / activity", label_da: "Sport / aktivitet", emoji: "⚽️", tint: "#C9F0CB" },
  { key: "dinner", label: "Dinner", label_da: "Middag", emoji: "🍽️", tint: "#FFB0A0" },
  { key: "social", label: "Social", label_da: "Socialt", emoji: "☕", tint: "#E8C8DD" },
  { key: "event", label: "Event / party", label_da: "Begivenhed / fest", emoji: "🎉", tint: "#ECD5F5" },
  { key: "day_trip", label: "Day trip", label_da: "Udflugt", emoji: "🎡", tint: "#FFC9A8" },
  { key: "school_event", label: "School / daycare event", label_da: "Skole / institution", emoji: "🎭", tint: "#FFEAA0" },
  // Kids — pickups, drop-offs, playdates. Only shown on the kind
  // picker for families with at least one under-11 (see
  // `visibleEventKinds` in lib/events.js). Teen+ households don't
  // have playdate flows, so hiding it keeps the picker honest.
  { key: "kids", label: "Kids", label_da: "Børn", emoji: "🧒", tint: "#FFE0BC" },
  { key: "travel", label: "Travel", label_da: "Rejse", emoji: "✈️", tint: "#CFE2F5" },
  { key: "appointment", label: "Appointment", label_da: "Aftale", emoji: "📅", tint: "#CCEDE3" },
  // Cool slate-blue for work — distinct from `travel`'s sky-blue and
  // `appointment`'s warm yellow, with an "office shirt" feel that
  // doesn't read as childish on the kitchen display.
  { key: "work", label: "Work", label_da: "Arbejde", emoji: "💼", tint: "#D8E3EB" },
  { key: "birthday", label: "Birthday", label_da: "Fødselsdag", emoji: "🎂", tint: "#FFD2DD" },
  { key: "other", label: "Other", label_da: "Andet", emoji: "•", tint: "#EFE4D2" },
];

// Localised label for a kind. Pass the active i18n `lang` ("en" or "da").
// Falls back to the English label when a translation is missing so we
// never render an empty pill.
export function kindLabel(kindOrKey, lang = "en") {
  const k = typeof kindOrKey === "string"
    ? EVENT_KINDS.find((x) => x.key === kindOrKey)
    : kindOrKey;
  if (!k) return "";
  if (lang === "da" && k.label_da) return k.label_da;
  return k.label;
}

// Filter to the kind list we show in pickers. The `kids` kind is
// scoped to families that actually have under-11 members — hiding
// it from teen-only households keeps the picker short and relevant.
// Pass `{ hasKid: true|false }` based on the family composition.
export function visibleEventKinds({ hasKid } = { hasKid: true }) {
  return EVENT_KINDS.filter((k) => k.key !== "kids" || hasKid);
}

// Keyword → kind dictionary for the auto-pick feature in the event
// editor. Entries are matched as case-insensitive word-boundary
// substrings against the event title. Order matters — the first
// kind that claims a keyword wins. The editor only applies a
// suggestion when the user hasn't already manually picked a kind
// (i.e. still on the default `other`), so typing "pizza night" in a
// new event nudges you to 🍽️ Dinner, but if you deliberately chose
// "Work" first, we leave it alone. Keywords live here — single
// source of truth — so Settings > Kinds can one day surface the
// learned vocabulary to the user.
export const KIND_KEYWORDS = {
  activity: [
    // English
    "gym", "workout", "training", "practice", "football", "soccer",
    "basketball", "golf", "handball", "tennis", "swimming", "swim",
    "run", "running", "yoga", "pilates",
    "dance class", "dance", "match", "game", "match day",
    // Danish
    "træning", "kamp", "løb", "svømning", "fodbold", "håndbold",
    "fitness", "dans",
    // French
    "entraînement", "combat", "course à pied", "natation",
    // Spanish
    "entrenamiento", "lucha", "correr", "nadar", "fútbol",
    // German
    "kämpfen", "laufen", "schwimmen", "fußball",
    // Swedish
    "kampsport", "löpning", "simning", "fotboll", "handboll",
  ],
  dinner: [
    // English
    "dinner", "family dinner", "lunch", "breakfast", "brunch", "bbq",
    "grill", "takeaway", "takeout", "burger", "pizza night", "pizza",
    "date night", "eat out", "restaurant",
    // Danish
    "middag", "frokost", "aftensmad", "morgenmad", "ud at spise",
    // Swedish
    "frukost", "restaurang", "äta", "mat",
    // French
    "dîner", "déjeuner", "petit-déjeuner", "barbecue", "manger",
    "nourriture",
    // Spanish
    "cena", "almuerzo", "desayuno", "barbacoa", "restaurante",
    "comer", "comida",
    // German
    "abendessen", "mittagessen", "frühstück", "grillen", "essen",
  ],
  social: [
    // English
    "coffee", "coffee date", "walk & talk", "walk and talk", "hangout",
    "catch up", "catchup", "drinks", "after work drinks", "double date",
    "date", "visit", "guests over", "guests", "game night", "movie night",
    "meet up", "meetup", "wine night",
    // Danish
    "kaffe", "hygge",
    // French
    "café", "rendez-vous", "passer du temps ensemble", "se retrouver",
    // German
    "kaffee", "treffen", "sich austauschen", "verabredung",
    // Spanish — "cita" is ambiguous (date / medical appointment);
    // declared first here so bare "cita" lands on social. Compound
    // titles like "cita con el dentista" still win appointment via
    // the longest-keyword rule ("dentista" > "cita").
    "cita", "pasar el rato", "ponerse al día", "coffe",
    // Swedish
    "dejt", "häng", "komma ikapp",
  ],
  event: [
    // English
    "party", "birthday party", "birthday brunch", "birthday dinner",
    "birthday lunch", "birthday drinks", "birthday cake",
    "wedding", "reception", "celebration",
    "get together", "get-together", "house party", "festival", "concert",
    "clubbing", "night out", "bar",
    // Danish
    "fest", "bryllup", "barnedåb", "housewarming",
    // French
    "anniversaire", "mariage", "boissons", "célébration",
    // Spanish
    "fiesta", "boda", "celebración", "concierto",
    // Swedish
    "bröllop", "firande", "konsert",
    // German
    "hochzeit", "getränke", "feier", "konzert",
  ],
  day_trip: [
    // English
    "zoo", "museum", "amusement park", "disney world", "disneyland",
    "disney land", "water park", "water world", "city trip", "family trip",
    // Danish
    "zoologisk have", "den blå planet", "sommerland", "vandland",
    // Spanish
    "zoológico", "museo",
  ],
  school_event: [
    // English
    "parent meeting", "school meeting", "teacher meeting", "performance",
    "school play", "field trip", "graduation", "daycare event",
    "daycare meeting", "daycare", "school", "class event",
    // Danish
    "skole", "børnehave", "forældremøde", "dagpleje", "vuggestue",
    // French
    "école", "maternelle", "réunion de parents", "garderie", "crèche",
    // Swedish
    "skola", "dagis", "föräldramöte", "daghem", "förskola",
    // German
    "schule", "kindergarten", "elternabend", "kindertagesstätte", "krippe",
    // Spanish
    "escuela", "jardín de infancia", "reunión de padres", "guardería",
    "jardín infantil",
  ],
  kids: [
    // English
    "playdate", "play date", "pickup", "pick up", "dropoff", "drop off",
    "school pickup", "school dropoff", "daycare pickup", "daycare dropoff",
    "after school", "kids party", "parent duty", "babysitter", "bedtime",
    // Danish
    "afhentning", "aflevering", "legeaftale",
    // Spanish
    "cita para jugar", "niñera", "niños", "recogida escolar",
    // German
    "abholung", "bringen", "spielverabredung", "kinder", "schulabholung",
    // French
    "dépose-minute", "prise en charge", "rendez-vous de jeu",
    "baby-sitter", "enfants", "accompagnement scolaire",
    // Swedish
    "upphämtning", "avlämning", "lekdejt", "barnvakt", "barn",
    "skolupphämtning", "lekavtal",
  ],
  travel: [
    // English
    "flight", "airport", "departure", "arrival", "hotel", "check-in",
    "check in", "check-out", "check out", "vacation", "holiday",
    "road trip", "trip", "train",
    "paris", "rome", "new york", "miami", "copenhagen", "berlin",
    "barcelona", "bali", "thailand", "mallorca",
    "france", "spain", "italy", "london", "usa",
    // Danish
    "fly", "flyvning", "ferie", "lufthavn", "sommerhus",
    // Spanish
    "vuelo", "aeropuerto", "vacaciones", "viaje",
    // German
    "flug", "flughafen", "urlaub", "ferien", "reise", "zug", "hütte",
    // Swedish
    "flyg", "flygplats", "hotell", "semester", "resa", "stuga",
    // French
    "vol", "aéroport", "hôtel", "vacances", "voyage",
  ],
  appointment: [
    // English
    "dentist", "doctor", "gp", "hospital", "check-up", "checkup",
    "therapy", "psychologist", "physio", "massage", "hairdresser",
    "barber", "nails", "appointment", "consultation", "vaccination",
    "scan",
    // Danish
    "læge", "tandlæge", "frisør", "terapi", "psykolog",
    // Spanish
    "dentista", "médico", "terapia", "fisioterapia", "peluquero",
    "barbero",
    // German
    "zahnarzt", "arzt", "krankenhaus", "therapie", "physiotherapie",
    "termin", "friseur", "barbier",
    // Swedish
    "tandläkare", "läkare", "sjukhus", "fysioterapeut", "frisör",
    "barberare",
    // French
    "dentiste", "médecin", "hôpital", "thérapie", "kinésithérapeute",
    "coiffeur",
  ],
  work: [
    // English
    "meeting", "work meeting", "1:1", "deadline", "presentation", "call",
    "zoom", "teams", "team", "business", "shift", "office",
    "client meeting", "interview", "work",
    // Danish
    "arbejde", "møde", "kontor",
    // French
    "réunion", "travail", "équipes", "bureau", "échéance", "présentation",
    // Swedish
    "möte", "arbete",
    // German
    "besprechung", "arbeit", "büro", "abgabetermin", "präsentation",
    // Spanish
    "reunión", "trabajo", "equipos", "oficina", "fecha límite",
    "presentación",
  ],
  // The longest-match-wins rule above means "birthday party"
  // (15 chars, → event) still beats "birthday" (8, → birthday) for
  // titles like "Liam birthday party". Bare "[name] birthday" or
  // "[name]s bday" lands here. Localised single-word birthday
  // terms (fødselsdag, anniversaire, cumpleaños, Geburtstag,
  // födelsedag) follow the same rule.
  birthday: [
    "birthday", "bday",
    "fødselsdag",           // Danish
    "anniversaire",         // French
    "cumpleaños",           // Spanish
    "geburtstag",           // German
    "födelsedag",           // Swedish
  ],
  other: [
    "dry cleaning", "dry clean", "errands", "shopping", "cleaning",
  ],
};

// Best-effort guess of an event kind from a free-text title. Returns
// null if nothing obvious matches so the caller can keep whatever
// default was already in the draft. Uses word-boundary-ish matches
// so "dance" doesn't false-hit inside "bridge dance club" when the
// user meant something else, but "dance class" and "swim" still
// catch. Case-insensitive.
//
// Longest matching keyword wins across kinds — so "school pickup"
// (14 chars, kids) beats "school" (6 chars, school_event), which
// matches user intent. Ties fall back to EVENT_KINDS declaration
// order.
export function detectKindFromTitle(title, { hasKid = true } = {}) {
  const t = (title || "").toLowerCase().trim();
  if (t.length < 2) return null;
  const wordy = /[a-zæøå]/i;
  let best = null; // { kind, len, order }
  for (let order = 0; order < EVENT_KINDS.length; order++) {
    const kind = EVENT_KINDS[order];
    if (kind.key === "kids" && !hasKid) continue;
    const kws = KIND_KEYWORDS[kind.key] || [];
    for (const kw of kws) {
      const lower = kw.toLowerCase();
      let idx = t.indexOf(lower);
      while (idx !== -1) {
        const before = idx === 0 ? " " : t[idx - 1];
        const after = idx + lower.length >= t.length ? " " : t[idx + lower.length];
        if (!wordy.test(before) && !wordy.test(after)) {
          if (
            !best ||
            lower.length > best.len ||
            (lower.length === best.len && order < best.order)
          ) {
            best = { kind: kind.key, len: lower.length, order };
          }
          break; // first hit in this kw is enough; keep scanning other kws
        }
        idx = t.indexOf(lower, idx + 1);
      }
    }
  }
  return best ? best.kind : null;
}

export const kindOf = (k) =>
  EVENT_KINDS.find((x) => x.key === k) || EVENT_KINDS[EVENT_KINDS.length - 1];

// Prefer an event's custom emoji override (e.g. 🎵 on an Other event)
// over the kind's default. Keeps the kind's tint, so a "music class"
// Other event can wear a 🎵 and still inherit the neutral beige tint.
export const eventEmoji = (event) => {
  if (!event) return "•";
  const custom = (event.emoji || "").trim();
  if (custom) return custom;
  return kindOf(event.kind).emoji;
};

// ---------- datetime helpers ----------

// Convert a datetime-local value (YYYY-MM-DDTHH:mm) to an ISO string in UTC
export function datetimeLocalToIso(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Convert ISO → datetime-local (for <input type="datetime-local">)
export function isoToDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export function fmtEventWhen(iso, endIso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const isDa = lang === "da";
  const dateLocale = isDa ? "da-DK" : "en-US";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();

  const timeStr = fmtTime(d);
  const dateLabel = sameDay
    ? (isDa ? "I dag" : "Today")
    : isTomorrow
    ? (isDa ? "I morgen" : "Tomorrow")
    : d.toLocaleDateString(dateLocale, { weekday: "short", month: "short", day: "numeric" });

  // All-day events (time exactly 00:00) just show the date
  const isAllDay = d.getHours() === 0 && d.getMinutes() === 0;

  // Multi-day range (e.g. travel)
  if (endIso) {
    const e = new Date(endIso);
    if (!Number.isNaN(e.getTime())) {
      const startDay = `${d.getMonth()}-${d.getDate()}`;
      const endDay = `${e.getMonth()}-${e.getDate()}`;
      if (startDay !== endDay) {
        // Same-month ranges collapse to "May 14 → 17" so a weekend
        // trip doesn't blow up to two lines on a phone (the long
        // "Thu, May 14 → Sun, May 17" form forced wrapping that
        // pushed attendee avatars on top of the date text). Cross-
        // month ranges keep both labels in full.
        const sameMonth =
          d.getMonth() === e.getMonth() &&
          d.getFullYear() === e.getFullYear();
        if (sameMonth) {
          const startCompact = sameDay
            ? (isDa ? "I dag" : "Today")
            : isTomorrow
              ? (isDa ? "I morgen" : "Tomorrow")
              : d.toLocaleDateString(dateLocale, {
                  month: "short",
                  day: "numeric",
                });
          return `${startCompact} → ${e.getDate()}`;
        }
        const endLabel = e.toLocaleDateString(dateLocale, {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        return `${dateLabel} → ${endLabel}`;
      }
    }
  }
  if (isAllDay) return dateLabel;
  // Danish idiom uses "kl." (klokken) between the day and the time —
  // "I dag kl. 15.00" reads far more natural than "I dag · 15.00".
  return isDa
    ? `${dateLabel} kl. ${timeStr}`
    : `${dateLabel} · ${timeStr}`;
}

// Short relative countdown for the dashboard strip. Reads like a human
// glancing at the date: "in 3 days" / "tomorrow" / "in 2 hours" / "in 40 min" /
// "starting" / "now" / "just started" / "ended". Stops lying about precision
// — if something's a week away we don't say "in 10,080 minutes".
export function eventCountdown(iso, endIso) {
  if (!iso) return null;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return null;
  const end = endIso ? new Date(endIso) : null;
  const now = new Date();
  const msToStart = start - now;
  const msToEnd = end && !Number.isNaN(end.getTime()) ? end - now : null;

  // In-progress events get "ongoing" — distinct from "now".
  if (msToStart <= 0 && msToEnd != null && msToEnd > 0) return "ongoing";
  // All-day events (no ends_at) that started earlier today shouldn't read
  // "ended" — birthdays and similar run until midnight. Treat them as "today".
  const sameDay =
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate();
  if (end == null && msToStart < 0 && sameDay) return "today";
  // Past events — rarely surfaced in the upcoming strip, but safe.
  if (msToStart < -60 * 1000 && (msToEnd == null || msToEnd < 0)) return "ended";
  if (Math.abs(msToStart) < 60 * 1000) return "now";

  const minutes = Math.round(msToStart / 60000);
  if (minutes < 60 && minutes > 0) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24 && hours > 0) return `in ${hours}h`;
  // Day-aware diff so "tomorrow" doesn't become "in 24h" at 23:30.
  const startMid = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const nowMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((startMid - nowMid) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 365) return `in ${days} days`;
  return `in ${Math.round(days / 365)} years`;
}

/**
 * Locale-aware display wrapper for `eventCountdown`. Callers that need
 * semantic checks (e.g. `countdown === "ongoing"`) should still use
 * `eventCountdown` directly — the English tokens are the internal
 * enum. This helper only handles the *rendering*, so a Danish
 * dashboard can show "om 3 dage" instead of "in 3 days" without
 * touching a dozen call-sites' comparison logic.
 */
export function localizeCountdown(token, lang = "en") {
  if (!token) return token;
  if (lang !== "da") return token;
  // Exact matches first.
  if (token === "ongoing") return "i gang";
  if (token === "today") return "i dag";
  if (token === "tomorrow") return "i morgen";
  if (token === "now") return "nu";
  if (token === "ended") return "afsluttet";
  // Templated tails.
  let m = token.match(/^in (\d+) min$/);
  if (m) return `om ${m[1]} min`;
  m = token.match(/^in (\d+)h$/);
  if (m) return `om ${m[1]} t`;
  m = token.match(/^in (\d+) days$/);
  if (m) return `om ${m[1]} dage`;
  m = token.match(/^in (\d+) years$/);
  if (m) return `om ${m[1]} år`;
  return token;
}

export function groupEventsByDay(events) {
  const groups = new Map();
  for (const ev of events) {
    const d = new Date(ev.starts_at);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ev);
  }
  return Array.from(groups.entries()).map(([date, items]) => ({
    date,
    items: items.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
  }));
}

export function dayLabel(key, lang = "en") {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const now = new Date();
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const isDa = lang === "da";
  if (same(dt, now)) return isDa ? "I dag" : "Today";
  const tmrw = new Date(now);
  tmrw.setDate(now.getDate() + 1);
  if (same(dt, tmrw)) return isDa ? "I morgen" : "Tomorrow";
  return dt.toLocaleDateString(isDa ? "da-DK" : "en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

// An event is "now" when it has started and not yet ended.
// If there is no ends_at, it's "now" for up to 60 minutes after start.
const DEFAULT_NOW_WINDOW_MS = 60 * 60 * 1000;

export function isNowEvent(event) {
  if (!event?.starts_at) return false;
  const start = new Date(event.starts_at).getTime();
  if (Number.isNaN(start)) return false;
  const now = Date.now();
  if (start > now) return false;
  const end = event.ends_at
    ? new Date(event.ends_at).getTime()
    : start + DEFAULT_NOW_WINDOW_MS;
  return now <= end;
}

// True when the attendee list either is empty (implicit everyone) OR
// contains every non-pet family member. In both cases, individual
// "I'm there" acknowledgments are noise.
export function isWholeFamilyEvent(event, members) {
  const humans = (members || []).filter((m) => m.role !== "pet");
  if (humans.length === 0) return true;
  const ids = event.member_ids || [];
  if (ids.length === 0) return true;
  const set = new Set(ids);
  return humans.every((m) => set.has(m.id));
}

// Who can ack this event: only members in the attendee list (non-pets).
export function eligibleAckMemberIds(event, members) {
  const humans = (members || []).filter((m) => m.role !== "pet");
  const ids = new Set(event.member_ids || []);
  return humans.filter((m) => ids.has(m.id)).map((m) => m.id);
}

// Generate synthetic (virtual) birthday events from members for display in
// the calendar. These are non-editable — `source: "birthday"` marks them.
// Yields birthdays occurring in [now, now + days] (default 400d so every member
// appears at least once).
export function birthdayEventsFrom(members, { days = 400 } = {}) {
  const out = [];
  const now = new Date();
  const horizon = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  for (const m of members || []) {
    if (!m.birthday) continue;
    const parts = m.birthday.split("-");
    if (parts.length !== 3) continue;
    const [yRaw, moRaw, dRaw] = parts;
    const mo = Number(moRaw);
    const d = Number(dRaw);
    if (!(mo >= 1 && mo <= 12 && d >= 1 && d <= 31)) continue;
    const knownYear = yRaw !== "0000" ? Number(yRaw) : null;
    // Find the next occurrence from today
    let year = now.getFullYear();
    let nextDate = new Date(year, mo - 1, d, 0, 0, 0, 0);
    if (nextDate < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
      year += 1;
      nextDate = new Date(year, mo - 1, d, 0, 0, 0, 0);
    }
    if (nextDate > horizon) continue;
    const age = knownYear ? year - knownYear : null;
    out.push({
      id: `bday-${m.id}-${year}`,
      title: age ? `${m.name}'s birthday · turns ${age}` : `${m.name}'s birthday`,
      starts_at: nextDate.toISOString(),
      ends_at: null,
      kind: "birthday",
      member_ids: [m.id],
      notes: null,
      created_at: nextDate.toISOString(),
      arrivals: {},
      skipped_by: [],
      source: "birthday", // <-- virtual marker
    });
  }
  return out;
}

// Merge real events (from API) with synthetic birthday events, de-duped by id,
// and sorted by starts_at ASC.
export function mergeWithBirthdays(realEvents, members, opts) {
  const synth = birthdayEventsFrom(members, opts);
  const byId = new Map();
  for (const e of [...(realEvents || []), ...synth]) {
    if (!byId.has(e.id)) byId.set(e.id, e);
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.starts_at.localeCompare(b.starts_at)
  );
}

// Pick events for the dashboard strip.
// Rules:
//  - Always include ALL of today's events (even if more than `targetMin`).
//  - If total count is still below `targetMin`, add the next day in full,
//    and continue day-by-day until we reach `targetMin` or run out.
//  - Hard cap total at `hardMax` to keep the strip manageable.
export function selectStripEvents(mergedEvents, { targetMin = 4, hardMax = 12 } = {}) {
  if (!mergedEvents || mergedEvents.length === 0) return [];
  // Only consider events that haven't fully ended yet
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const future = mergedEvents.filter((ev) => {
    const end = new Date(ev.ends_at || ev.starts_at);
    return end >= startOfToday;
  });
  // Group by calendar day (local) preserving the existing order
  const buckets = new Map();
  for (const ev of future) {
    const d = new Date(ev.starts_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(ev);
  }
  const dayKeys = Array.from(buckets.keys()).sort();
  const out = [];
  for (const key of dayKeys) {
    out.push(...buckets.get(key));
    if (out.length >= targetMin) break;
  }
  return out.slice(0, hardMax);
}
